<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import API from 'dashboard/api/shopifyOrderActions';

const props = defineProps({
  params: { type: Object, required: true },
  disabled: { type: Boolean, default: false },
  available: { type: Boolean, default: true },
});
defineEmits(['add']);
const { t } = useI18n();
const query = ref('');
const activeQuery = ref('');
const products = ref([]);
const pageInfo = ref({});
const loading = ref(false);
const error = ref('');
const brokenImages = ref({});
let requestId = 0;
let retryMore = false;
const thumbnail = item =>
  item.media?.nodes?.[0]?.preview?.image ||
  item.product.featuredMedia?.preview?.image;

const search = async (more = false) => {
  if (!props.available) return;
  requestId += 1;
  const id = requestId;
  retryMore = more;
  loading.value = true;
  error.value = '';
  if (!more) {
    activeQuery.value = query.value.trim();
    products.value = [];
    pageInfo.value = {};
  }
  try {
    const { data } = await API.catalog({
      ...props.params,
      query: activeQuery.value,
      after: more ? pageInfo.value.endCursor : undefined,
    });
    if (id !== requestId) return;
    const rows = more ? [...products.value, ...data.nodes] : data.nodes;
    products.value = [...new Map(rows.map(item => [item.id, item])).values()];
    pageInfo.value = data.pageInfo || {};
  } catch (e) {
    if (id === requestId)
      error.value = e.response?.data?.error || t('ORDER_ACTIONS.ERROR');
  } finally {
    if (id === requestId) loading.value = false;
  }
};
watch(
  () => [props.params.hook_id, props.params.conversation_id, props.available],
  () => {
    requestId += 1;
    query.value = '';
    products.value = [];
    pageInfo.value = {};
    loading.value = false;
    error.value = '';
    if (props.available) search();
  },
  { immediate: true }
);
</script>

<template>
  <section class="flex min-w-0 flex-col gap-3 border-t border-n-weak pt-4">
    <h4 class="font-semibold">{{ t('ORDER_ACTIONS.CATALOG_TITLE') }}</h4>
    <div class="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
      <label class="flex min-w-0 flex-1 flex-col gap-1">
        {{ t('ORDER_ACTIONS.SEARCH') }}
        <input
          v-model="query"
          type="search"
          class="!mb-0 min-h-11 !text-base"
          :disabled="disabled || !available"
          @keydown.enter.prevent="search()"
          @search="search()"
        />
      </label>
      <Button
        type="button"
        :label="t('ORDER_ACTIONS.SEARCH_BUTTON')"
        class="min-h-11 shrink-0"
        variant="outline"
        icon="i-lucide-search"
        :disabled="disabled || !available || loading"
        @click="search()"
      />
    </div>
    <p v-if="loading" role="status">{{ t('ORDER_ACTIONS.CATALOG_LOADING') }}</p>
    <div v-if="error" role="alert" class="text-n-ruby-11">
      <p>{{ error }}</p>
      <Button
        type="button"
        :label="t('ORDER_ACTIONS.CATALOG_RETRY')"
        :disabled="disabled || loading"
        @click="search(retryMore)"
      />
    </div>
    <p v-if="available && !loading && !error && !products.length">
      {{ t('ORDER_ACTIONS.CATALOG_EMPTY') }}
    </p>
    <div
      class="grid max-h-96 min-w-0 grid-cols-1 gap-3 overflow-y-auto overscroll-contain sm:grid-cols-2"
    >
      <div
        v-for="product in products"
        :key="product.id"
        class="grid min-w-0 grid-cols-[4rem_minmax(0,1fr)] content-start items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-3"
      >
        <img
          v-if="thumbnail(product)?.url && !brokenImages[product.id]"
          :src="thumbnail(product).url"
          :alt="thumbnail(product).altText || product.product.title"
          class="h-16 w-16 rounded-lg bg-white object-contain"
          loading="lazy"
          @error="brokenImages[product.id] = true"
        />
        <span
          v-else
          class="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-n-alpha-3 text-center text-xs text-n-slate-11"
          >{{ t('ORDER_ACTIONS.CATALOG_NO_IMAGE') }}</span
        >
        <div class="min-w-0 break-words leading-5">
          <strong class="block font-medium">{{ product.product.title }}</strong>
          <p
            v-if="product.title !== 'Default Title'"
            class="!mb-0 mt-1 text-n-slate-11"
          >
            {{ product.title }}
          </p>
          <p v-if="product.sku" class="!mb-0 mt-1 text-xs text-n-slate-11">
            {{ product.sku }}
          </p>
        </div>
        <Button
          type="button"
          :label="t('ORDER_ACTIONS.ADD')"
          class="col-span-2 min-h-11 w-full"
          icon="i-lucide-plus"
          variant="outline"
          size="sm"
          :disabled="disabled || loading"
          @click="$emit('add', product)"
        />
      </div>
    </div>
    <Button
      v-if="pageInfo.hasNextPage"
      type="button"
      variant="outline"
      :label="t('ORDER_ACTIONS.CATALOG_MORE')"
      :disabled="disabled || loading"
      @click="search(true)"
    />
  </section>
</template>
